import {
  type FirstAccessPreview,
  isChatApiError,
} from '@alternativamais/chat-core';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../componentes/Avatar';
import { avisarErro, mensagemDeErro } from '../../componentes/Avisos';
import { Icone } from '../../componentes/Icone';
import { useSessao } from '../../chat/contexto';
import { CLASSE_CAMPO, lerImagem } from '../config/comum';
import { RecortarAvatar } from '../config/usuario/RecortarAvatar';
import { CampoSenha, RegrasDaSenha, senhaAceita } from './CampoSenha';

type Etapa = 'senha' | 'perfil' | 'pronto';

/** Primeiro nome, para o "Boas-vindas, Fernanda". */
const primeiroNome = (nome: string) => nome.trim().split(/\s+/)[0] ?? nome;

/**
 * Primeiro acesso (A2 → A16 → A17): pelo link que o painel gerou ou depois
 * de entrar com a senha inicial (`trocar`). Define a senha, monta o perfil
 * e deixa os avisos prontos; no fim abre o chat.
 */
export function PrimeiroAcesso({
  modo,
  token,
}: {
  modo: 'link' | 'trocar';
  token?: string;
}) {
  const { cliente, senhaInicial, concluirAcesso, sair } = useSessao();
  const navegar = useNavigate();
  const [etapa, setEtapa] = useState<Etapa>('senha');
  const [conta, setConta] = useState<FirstAccessPreview | null>(null);
  const [linkInvalido, setLinkInvalido] = useState<FalhaDoLink | null>(null);

  // Quem é: pelo link (antes de ter sessão) ou pela conta já logada.
  useEffect(() => {
    let vivo = true;
    const busca =
      modo === 'link' && token
        ? cliente.api.firstAccess(token)
        : cliente.api.me().then((eu) => ({
            account_id: 0,
            name: eu.global_name ?? eu.username,
            username: eu.username,
          }));
    busca
      .then((achada) => vivo && setConta(achada))
      .catch((erro) => {
        if (!vivo) return;
        setLinkInvalido(falhaDoLink(erro));
      });
    return () => {
      vivo = false;
    };
  }, [cliente, modo, token]);

  const terminar = async () => {
    navegar('/canais/@me', { replace: true });
    await concluirAcesso();
  };

  const voltarAoLogin = async () => {
    if (modo === 'trocar') await sair();
    navegar('/', { replace: true });
  };

  return (
    <div className="flex h-full flex-col overflow-y-auto bg-chat">
      <header className="flex shrink-0 items-center justify-between gap-4 px-6 py-5">
        <span className="flex items-center gap-2.5 font-titulo text-lg font-bold">
          <span className="flex size-9 items-center justify-center rounded-[10px] bg-destaque text-destaque-texto">
            <Icone nome="logo" tamanho={22} />
          </span>
          Alternativa Chat
        </span>
        <Passos etapa={etapa} />
      </header>
      <main className="flex grow items-start justify-center p-4 sm:items-center">
        <div className="flex w-[480px] max-w-full flex-col gap-5 rounded-xl bg-elevado p-8 shadow-[0_12px_32px_rgba(0,0,0,.45)]">
          {linkInvalido ? (
            <LinkInvalido falha={linkInvalido} aoVoltar={voltarAoLogin} />
          ) : !conta ? (
            <p className="m-0 text-texto-2">Carregando…</p>
          ) : etapa === 'senha' ? (
            <EtapaSenha
              conta={conta}
              modo={modo}
              senhaAtual={senhaInicial}
              aoVoltar={voltarAoLogin}
              aoDefinir={async (atual, nova) => {
                if (modo === 'link' && token) {
                  await cliente.completeFirstAccess(token, nova);
                } else {
                  await cliente.api.changePassword(atual ?? '', nova);
                }
                setEtapa('perfil');
              }}
            />
          ) : etapa === 'perfil' ? (
            <EtapaPerfil
              nomeInicial={conta.name}
              aoContinuar={(nome) => {
                if (nome) setConta({ ...conta, name: nome });
                setEtapa('pronto');
              }}
            />
          ) : (
            <EtapaPronto nome={conta.name} aoContinuar={terminar} />
          )}
        </div>
      </main>
    </div>
  );
}

function Passos({ etapa }: { etapa: Etapa }) {
  const ordem: Array<[Etapa, string]> = [
    ['senha', 'Senha'],
    ['perfil', 'Perfil'],
    ['pronto', 'Pronto'],
  ];
  const atual = ordem.findIndex(([id]) => id === etapa);
  return (
    <ol
      aria-label="Etapas do primeiro acesso"
      className="m-0 flex list-none items-center gap-2 p-0 text-sm"
    >
      {ordem.map(([id, rotulo], indice) => {
        const feita = indice < atual;
        return (
          <li
            key={id}
            aria-current={indice === atual ? 'step' : undefined}
            className={`flex items-center gap-2 ${indice === atual ? 'font-semibold text-texto' : 'text-texto-3'}`}
          >
            {indice > 0 && <span className="h-px w-5 bg-borda" />}
            <span
              className={`flex size-6 items-center justify-center rounded-full text-xs font-bold ${
                feita
                  ? 'bg-online text-black'
                  : indice === atual
                    ? 'bg-destaque text-destaque-texto'
                    : 'bg-ativo'
              }`}
            >
              {feita ? <Icone nome="check" tamanho={14} /> : indice + 1}
            </span>
            <span className="max-sm:sr-only">
              {rotulo}
              {feita && <span className="sr-only"> (concluída)</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

type FalhaDoLink = { titulo: string; texto: string };

/** Link vencido ou usado (404) é uma coisa; sem conexão é outra. */
function falhaDoLink(erro: unknown): FalhaDoLink {
  return isChatApiError(erro) && erro.status === 404
    ? {
        titulo: 'Este link não vale mais',
        texto:
          'Peça outro a quem administra o sistema. Links de primeiro acesso valem por 7 dias e funcionam uma vez só.',
      }
    : { titulo: 'Não deu para abrir o link', texto: mensagemDeErro(erro) };
}

function LinkInvalido({
  falha,
  aoVoltar,
}: {
  falha: FalhaDoLink;
  aoVoltar: () => void;
}) {
  return (
    <>
      <span className="flex size-12 items-center justify-center rounded-full bg-ativo text-ausente">
        <Icone nome="alerta" tamanho={24} />
      </span>
      <h1 className="m-0 font-titulo text-2xl font-bold">{falha.titulo}</h1>
      <p className="m-0 text-[15px] leading-normal text-texto-2">
        {falha.texto}
      </p>
      <button
        type="button"
        onClick={aoVoltar}
        className="flex h-11 items-center justify-center rounded-lg bg-destaque text-[15px] font-semibold text-destaque-texto hover:brightness-105"
      >
        Voltar ao login
      </button>
    </>
  );
}

function EtapaSenha({
  conta,
  modo,
  senhaAtual,
  aoVoltar,
  aoDefinir,
}: {
  conta: FirstAccessPreview;
  modo: 'link' | 'trocar';
  senhaAtual: string | null;
  aoVoltar: () => void;
  aoDefinir: (atual: string | null, nova: string) => Promise<void>;
}) {
  // Entrou agora com a senha inicial: não pede de novo.
  const pedirAtual = modo === 'trocar' && !senhaAtual;
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const identidade = { username: conta.username };
  const confere = !!confirmar && confirmar === nova;

  const enviar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (pedirAtual && !atual) return setErro('Digite a senha atual.');
    if (!senhaAceita(nova, identidade)) {
      return setErro('A senha nova ainda não cumpre todas as regras.');
    }
    if (!confere) return setErro('As senhas não conferem.');
    setEnviando(true);
    setErro(null);
    try {
      await aoDefinir(pedirAtual ? atual : senhaAtual, nova);
    } catch (falha) {
      const campos =
        isChatApiError(falha) &&
        falha.errors &&
        typeof falha.errors === 'object'
          ? (falha.errors as Record<string, string>)
          : null;
      setErro(
        campos?.password ??
          campos?.new_password ??
          (campos ? Object.values(campos)[0] : mensagemDeErro(falha)),
      );
      setEnviando(false);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={enviar} noValidate>
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 font-titulo text-[26px] leading-tight font-bold">
          {modo === 'link'
            ? `Boas-vindas, ${primeiroNome(conta.name)}`
            : 'Troque a senha inicial'}
        </h1>
        <p className="m-0 text-[15px] leading-normal text-texto-2">
          {modo === 'link'
            ? 'Crie a senha que você vai usar para entrar. '
            : 'A senha que você recebeu vale só para o primeiro acesso. '}
          Seu usuário é <strong className="text-texto">{conta.username}</strong>
          .
        </p>
      </div>
      {pedirAtual && (
        <CampoSenha
          rotulo="Senha atual"
          valor={atual}
          aoMudar={setAtual}
          autoComplete="current-password"
          autoFocus
        />
      )}
      <CampoSenha
        rotulo="Nova senha"
        valor={nova}
        aoMudar={setNova}
        autoFocus={!pedirAtual}
      />
      <RegrasDaSenha senha={nova} identidade={identidade} />
      <CampoSenha
        rotulo="Confirmar senha"
        valor={confirmar}
        aoMudar={setConfirmar}
        invalido={!!confirmar && !confere}
      />
      {confirmar && (
        <p
          className={`m-0 text-[13px] ${confere ? 'text-online' : 'text-ocupado'}`}
        >
          {confere ? 'As senhas conferem.' : 'As senhas não conferem.'}
        </p>
      )}
      {erro && (
        <p
          role="alert"
          className="m-0 flex items-start gap-1.5 text-[13px] leading-[1.45] text-ocupado"
        >
          <Icone nome="alerta" tamanho={16} className="mt-px shrink-0" />
          <span>{erro}</span>
        </p>
      )}
      <div className="mt-1 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={aoVoltar}
          className="text-sm font-semibold text-texto-2 hover:text-texto hover:underline"
        >
          Voltar ao login
        </button>
        <button
          type="submit"
          disabled={enviando}
          className="flex h-11 items-center justify-center rounded-lg bg-destaque px-6 text-[15px] font-semibold text-destaque-texto hover:brightness-105 disabled:opacity-60"
        >
          {enviando ? 'Salvando…' : 'Continuar'}
        </button>
      </div>
    </form>
  );
}

function EtapaPerfil({
  nomeInicial,
  aoContinuar,
}: {
  nomeInicial: string;
  /** Com o nome salvo (para o "Tudo certo, …"); pulando, sem nada. */
  aoContinuar: (nome?: string) => void;
}) {
  const { cliente } = useSessao();
  const [nome, setNome] = useState(nomeInicial);
  const [foto, setFoto] = useState<string | null>(null);
  const [recortar, setRecortar] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  const salvar = async () => {
    setEnviando(true);
    try {
      await cliente.api.modifyMe({
        global_name: nome.trim() || null,
        ...(foto ? { avatar: foto } : {}),
      });
      aoContinuar(nome.trim() || undefined);
    } catch (erro) {
      avisarErro(erro);
      setEnviando(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 font-titulo text-[26px] leading-tight font-bold">
          Como a equipe vai te ver
        </h1>
        <p className="m-0 text-[15px] leading-normal text-texto-2">
          Nome e foto aparecem ao lado das suas mensagens. Dá para mudar depois
          em Configurações › Perfil.
        </p>
      </div>
      <div className="flex items-center gap-4">
        {foto ? (
          <img
            src={foto}
            alt=""
            className="size-20 rounded-full object-cover"
          />
        ) : (
          <Avatar
            pessoa={{ id: '0', avatar: null }}
            nome={nome || nomeInicial}
            tamanho={80}
          />
        )}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => entrada.current?.click()}
            className="flex h-9 items-center gap-2 rounded-lg bg-ativo px-3.5 text-sm font-semibold hover:brightness-110"
          >
            <Icone nome="imagem" tamanho={16} />
            {foto ? 'Trocar foto' : 'Enviar foto'}
          </button>
          <span className="text-[13px] text-texto-3">
            JPG ou PNG. Sem foto, ficam as iniciais.
          </span>
        </div>
        <input
          ref={entrada}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          hidden
          onChange={(evento) => {
            const arquivo = evento.target.files?.[0];
            evento.target.value = '';
            if (arquivo) lerImagem(arquivo).then(setRecortar).catch(avisarErro);
          }}
        />
      </div>
      <label className="flex flex-col gap-2">
        <span className="flex items-center text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
          Nome de exibição
          <span className="ml-auto font-normal tracking-normal normal-case tabular-nums">
            {nome.length}/32
          </span>
        </span>
        <input
          maxLength={32}
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
          className={CLASSE_CAMPO}
        />
      </label>
      <p className="m-0 flex items-start gap-2 rounded-lg bg-chat p-3 text-[13px] leading-normal text-texto-2">
        <Icone
          nome="info"
          tamanho={16}
          className="mt-px shrink-0 text-texto-3"
        />
        Setor e cargos são definidos por quem administra o servidor. Para mudar,
        fale com sua supervisão.
      </p>
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => aoContinuar()}
          className="text-sm font-semibold text-texto-2 hover:text-texto hover:underline"
        >
          Pular
        </button>
        <button
          type="button"
          disabled={enviando}
          onClick={() => void salvar()}
          className="flex h-11 items-center justify-center rounded-lg bg-destaque px-6 text-[15px] font-semibold text-destaque-texto hover:brightness-105 disabled:opacity-60"
        >
          {enviando ? 'Salvando…' : 'Continuar'}
        </button>
      </div>
      {recortar && (
        <RecortarAvatar
          imagem={recortar}
          aoFechar={() => setRecortar(null)}
          aoAplicar={(dataUri) => {
            setFoto(dataUri);
            setRecortar(null);
          }}
        />
      )}
    </>
  );
}

function EtapaPronto({
  nome,
  aoContinuar,
}: {
  nome: string;
  aoContinuar: () => Promise<void>;
}) {
  const suporta = typeof Notification !== 'undefined';
  const [permissao, setPermissao] = useState(
    suporta ? Notification.permission : 'denied',
  );
  const [abrindo, setAbrindo] = useState(false);
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 font-titulo text-[26px] leading-tight font-bold">
          Tudo certo, {primeiroNome(nome)}!
        </h1>
        <p className="m-0 text-[15px] leading-normal text-texto-2">
          Sua conta está pronta. Antes de entrar, deixe os avisos chegando até
          você.
        </p>
      </div>
      {suporta && (
        <div className="flex items-center gap-3 rounded-lg border border-borda bg-chat p-4">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-destaque-suave text-destaque">
            <Icone nome="sino" tamanho={20} />
          </span>
          <div className="flex min-w-0 grow flex-col gap-0.5">
            <span className="font-semibold">Receba avisos</span>
            <span className="text-[13px] text-texto-2">
              Menções e mensagens diretas chegam mesmo com a aba em segundo
              plano.
            </span>
          </div>
          {permissao === 'granted' ? (
            <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-online">
              <Icone nome="check" tamanho={16} />
              Ativados
            </span>
          ) : (
            <button
              type="button"
              disabled={permissao === 'denied'}
              onClick={() =>
                void Notification.requestPermission().then(setPermissao)
              }
              className="h-9 shrink-0 rounded-lg bg-ativo px-3.5 text-sm font-semibold hover:brightness-110 disabled:opacity-50"
            >
              {permissao === 'denied' ? 'Bloqueados' : 'Ativar notificações'}
            </button>
          )}
        </div>
      )}
      <p className="m-0 text-[13px] text-texto-3">
        Dá para fazer isso depois em Configurações.
      </p>
      <button
        type="button"
        disabled={abrindo}
        onClick={() => {
          setAbrindo(true);
          aoContinuar().catch((erro) => {
            avisarErro(erro);
            setAbrindo(false);
          });
        }}
        className="flex h-11 items-center justify-center rounded-lg bg-destaque text-[15px] font-semibold text-destaque-texto hover:brightness-105 disabled:opacity-60"
      >
        {abrindo ? 'Abrindo…' : 'Continuar'}
      </button>
    </>
  );
}
